import { createAction, props } from '@ngrx/store';
import { PaginationResponse } from '@shared/models/common.model';
import { WalletListItem } from '@shared/models/wallet.model';
import {
  Asset,
  CreateWalletRequest,
  UpdateWalletRequest,
  WalletTransactionsQuery,
  WalletTransactionsResponse,
  WalletWithdrawalPrevalidateResponse,
} from '../models/wallet.model';

// Load Wallets Actions
export const loadWallets = createAction(
  '[Wallet] Load Wallets',
  props<{ vaultId: string; page: number; limit: number; append?: boolean }>()
);

export const loadWalletsSuccess = createAction(
  '[Wallet] Load Wallets Success',
  props<{
    wallets: WalletListItem[];
    pagination: PaginationResponse;
    append: boolean;
  }>()
);

export const loadWalletsFailure = createAction(
  '[Wallet] Load Wallets Failure',
  props<{ error: string }>()
);

// Load Assets Actions
export const loadAssets = createAction('[Wallet] Load Assets');

export const loadAssetsSuccess = createAction(
  '[Wallet] Load Assets Success',
  props<{ assets: Asset[] }>()
);

export const loadAssetsFailure = createAction(
  '[Wallet] Load Assets Failure',
  props<{ error: string }>()
);

// Create Wallet Actions
export const createWallet = createAction(
  '[Wallet] Create Wallet',
  props<{ request: CreateWalletRequest }>()
);

export const createWalletSuccess = createAction(
  '[Wallet] Create Wallet Success',
  props<{ message: string; requestId: string }>()
);

export const createWalletFailure = createAction(
  '[Wallet] Create Wallet Failure',
  props<{ error: string }>()
);

// Update Wallet Actions
export const updateWallet = createAction(
  '[Wallet] Update Wallet',
  props<{ walletId: string; request: UpdateWalletRequest }>()
);

export const updateWalletSuccess = createAction(
  '[Wallet] Update Wallet Success',
  props<{ message: string; requestId: string }>()
);

export const updateWalletFailure = createAction(
  '[Wallet] Update Wallet Failure',
  props<{ error: string }>()
);

// Disable Wallet Actions
export const disableWallet = createAction(
  '[Wallet] Disable Wallet',
  props<{ walletId: string }>()
);

export const disableWalletSuccess = createAction(
  '[Wallet] Disable Wallet Success',
  props<{ message: string; requestId: string }>()
);

export const disableWalletFailure = createAction(
  '[Wallet] Disable Wallet Failure',
  props<{ error: string }>()
);

// Enable Wallet Actions
export const enableWallet = createAction(
  '[Wallet] Enable Wallet',
  props<{ walletId: string }>()
);

export const enableWalletSuccess = createAction(
  '[Wallet] Enable Wallet Success',
  props<{ message: string; requestId: string }>()
);

export const enableWalletFailure = createAction(
  '[Wallet] Enable Wallet Failure',
  props<{ error: string }>()
);

// Retry a failed key generation (vault-management V-7)
export const retryWalletKeygen = createAction(
  '[Wallet] Retry Wallet Keygen',
  props<{ walletId: string }>()
);

export const retryWalletKeygenSuccess = createAction(
  '[Wallet] Retry Wallet Keygen Success',
  props<{ message: string }>()
);

export const retryWalletKeygenFailure = createAction(
  '[Wallet] Retry Wallet Keygen Failure',
  props<{ error: string }>()
);

// Clear operation success
export const clearOperationSuccess = createAction(
  '[Wallet] Clear Operation Success'
);

// Wallet Transactions Actions
export const loadWalletTransactions = createAction(
  '[Wallet] Load Wallet Transactions',
  props<{ walletId: string; query: WalletTransactionsQuery }>()
);

export const loadWalletTransactionsSuccess = createAction(
  '[Wallet] Load Wallet Transactions Success',
  props<{ response: WalletTransactionsResponse }>()
);

export const loadWalletTransactionsFailure = createAction(
  '[Wallet] Load Wallet Transactions Failure',
  props<{ error: string }>()
);

// Link to Wallet
export const linkWhitelistAddressToWallet = createAction(
  '[Wallet] Link Whitelist Address To Wallet',
  props<{ walletId: string; whitelistAddressId: string }>()
);

export const linkWhitelistAddressToWalletSuccess = createAction(
  '[Wallet] Link Whitelist Address To Wallet Success'
);

export const linkWhitelistAddressToWalletFailure = createAction(
  '[Wallet] Link Whitelist Address To Wallet Failure',
  props<{ error: string }>()
);

// Wallet-specific Enable/Disable Actions
export const disableWalletWhitelistAddress = createAction(
  '[WhitelistAddress] Disable Wallet Whitelist Address',
  props<{ walletId: string; whitelistAddressId: string }>()
);

export const disableWalletWhitelistAddressSuccess = createAction(
  '[WhitelistAddress] Disable Wallet Whitelist Address Success'
);

export const disableWalletWhitelistAddressFailure = createAction(
  '[WhitelistAddress] Disable Wallet Whitelist Address Failure',
  props<{ error: string }>()
);

export const enableWalletWhitelistAddress = createAction(
  '[WhitelistAddress] Enable Wallet Whitelist Address',
  props<{ walletId: string; whitelistAddressId: string }>()
);

export const enableWalletWhitelistAddressSuccess = createAction(
  '[WhitelistAddress] Enable Wallet Whitelist Address Success'
);

export const enableWalletWhitelistAddressFailure = createAction(
  '[WhitelistAddress] Enable Wallet Whitelist Address Failure',
  props<{ error: string }>()
);

// Unassign whitelist address from wallet (remove link)
export const unassignWhitelistAddressFromWallet = createAction(
  '[Wallet] Unassign Whitelist Address From Wallet',
  props<{ walletId: string; whitelistAddressId: string }>()
);

export const unassignWhitelistAddressFromWalletSuccess = createAction(
  '[Wallet] Unassign Whitelist Address From Wallet Success'
);

export const unassignWhitelistAddressFromWalletFailure = createAction(
  '[Wallet] Unassign Whitelist Address From Wallet Failure',
  props<{ error: string }>()
);

// Withdrawal Prevalidate
export const prevalidateWithdrawal = createAction(
  '[Wallet] Prevalidate Withdrawal',
  props<{ walletId: string }>()
);

export const prevalidateWithdrawalSuccess = createAction(
  '[Wallet] Prevalidate Withdrawal Success',
  props<{
    walletId: string;
    response: WalletWithdrawalPrevalidateResponse;
  }>()
);

export const prevalidateWithdrawalFailure = createAction(
  '[Wallet] Prevalidate Withdrawal Failure',
  props<{ walletId: string; error: string }>()
);
